import React, { useEffect, useMemo, useState } from 'react'
import {
  Collapse,
  Dialog,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined'
import { useTranslation } from 'react-i18next'
import { DatePicker } from 'rsuite'
import TagPriority from '../../components/common/TagPriority'
import TagPriorityRisks from '../../components/common/TagPriorityRisks'
import SelectPriorityRisks from '../../components/common/SelectPriorityRisks'
import SelectCriticality from '../../components/common/IncidentCriticality'
import { DetailButton } from '../../components/common/detail'
import {
  hasVisibleRiskName,
  sanitizeRiskText,
} from '../../utils/planImportRisksPayload'
import '../../components/common/detail/detail.css'
import './ImportRisksWizard.css'

export type WizardRisk = {
  id: string
  name: string
  description?: string
  criticality: number
  priority: number
  deadline?: Date | null
  source: 'ai' | 'manual'
}

type Step = 'review' | 'addMissing' | 'final'

const STEP_ORDER: Step[] = ['review', 'addMissing', 'final']

interface ImportRisksWizardProps {
  open: boolean
  risks: WizardRisk[]
  loading?: boolean
  onClose: () => void
  onConfirmImport: (risksToImport: WizardRisk[]) => Promise<void>
}

const ImportRisksWizard: React.FC<ImportRisksWizardProps> = ({
  open,
  risks,
  loading = false,
  onClose,
  onConfirmImport,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [step, setStep] = useState<Step>('review')
  const [selectedRiskIds, setSelectedRiskIds] = useState<Set<string>>(new Set())
  const [manualRisks, setManualRisks] = useState<WizardRisk[]>([])
  const [isAiListExpanded, setIsAiListExpanded] = useState(false)

  const [manualName, setManualName] = useState('')
  const [manualDescription, setManualDescription] = useState('')
  const [manualCriticality, setManualCriticality] = useState<number>(1)
  const [manualPriority, setManualPriority] = useState<number>(1)
  const [manualDeadline, setManualDeadline] = useState<Date | null>(new Date())
  const [manualNameError, setManualNameError] = useState('')

  useEffect(() => {
    if (!open) return
    setStep('review')
    setSelectedRiskIds(new Set(risks.map((risk) => risk.id)))
    setManualRisks([])
    setIsAiListExpanded(false)
    setManualName('')
    setManualDescription('')
    setManualCriticality(1)
    setManualPriority(1)
    setManualDeadline(new Date())
    setManualNameError('')
  }, [open, risks])

  const aiRisks = useMemo(() => risks.filter((risk) => risk.source === 'ai'), [risks])

  const allReviewRisks = useMemo(() => [...aiRisks, ...manualRisks], [aiRisks, manualRisks])

  const selectedCount = useMemo(() => {
    const selectedAi = aiRisks.filter((risk) => selectedRiskIds.has(risk.id)).length
    return selectedAi + manualRisks.length
  }, [aiRisks, manualRisks, selectedRiskIds])

  const selectedForImport = useMemo(() => {
    const selectedAi = aiRisks.filter((risk) => selectedRiskIds.has(risk.id))
    return [...selectedAi, ...manualRisks]
  }, [aiRisks, manualRisks, selectedRiskIds])

  const handleToggleAiRisk = (riskId: string) => {
    setSelectedRiskIds((prev) => {
      const next = new Set(prev)
      if (next.has(riskId)) {
        next.delete(riskId)
      } else {
        next.add(riskId)
      }
      return next
    })
  }

  const resetManualForm = () => {
    setManualName('')
    setManualDescription('')
    setManualCriticality(1)
    setManualPriority(1)
    setManualDeadline(new Date())
    setManualNameError('')
  }

  const handleAddManualRisk = () => {
    if (!manualName.trim()) {
      setManualNameError(t('planImportRisksWizard.validation.nameRequired'))
      return
    }

    const manualRisk: WizardRisk = {
      id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: manualName.trim(),
      description: manualDescription.trim(),
      criticality: manualCriticality,
      priority: manualPriority,
      deadline: manualDeadline,
      source: 'manual',
    }

    setManualRisks((prev) => [...prev, manualRisk])
    resetManualForm()
  }

  const handleRemoveManualRisk = (riskId: string) => {
    setManualRisks((prev) => prev.filter((risk) => risk.id !== riskId))
  }

  const handleManualPriorityChange = (event: SelectChangeEvent<number>) => {
    setManualPriority(Number(event.target.value))
  }

  const handleManualCriticalityChange = (event: SelectChangeEvent<number>) => {
    setManualCriticality(Number(event.target.value))
  }

  const handleManualDeadlineChange = (value: Date | null) => {
    setManualDeadline(value)
  }

  const handleConfirmImport = async () => {
    await onConfirmImport(selectedForImport)
  }

  const formatDeadline = (deadline?: Date | null) => {
    if (!deadline) return t('planImportRisksWizard.common.noDeadline')
    const parsed = deadline instanceof Date ? deadline : new Date(deadline)
    if (Number.isNaN(parsed.getTime())) {
      return t('planImportRisksWizard.common.noDeadline')
    }
    return parsed.toLocaleDateString()
  }

  const nameMissingInFinal = selectedForImport.some(
    (risk) => !hasVisibleRiskName(risk.name)
  )

  const stepIndex = STEP_ORDER.indexOf(step)
  const headTitle =
    step === 'review'
      ? t('planImportRisksWizard.step1.title')
      : step === 'addMissing'
        ? t('planImportRisksWizard.step2.title')
        : t('planImportRisksWizard.step3.title')
  const headSubtitle =
    step === 'review'
      ? t('planImportRisksWizard.step1.subtitle')
      : step === 'addMissing'
        ? t('planImportRisksWizard.step2.subtitle')
        : t('planImportRisksWizard.step3.subtitle')

  const stepStrip = [
    { key: 'review' as const, label: t('planImportRisksWizard.steps.review') },
    {
      key: 'addMissing' as const,
      label: t('planImportRisksWizard.steps.addMissing'),
    },
    { key: 'confirm' as const, label: t('planImportRisksWizard.steps.confirm') },
  ]

  const renderRiskRow = (
    risk: WizardRisk,
    options: { checked: boolean; disabled?: boolean; showNumber?: number }
  ) => {
    const title =
      sanitizeRiskText(risk.name) ||
      t('planImportRisksWizard.common.unnamedRisk')
    const description =
      sanitizeRiskText(risk.description) ||
      t('planImportRisksWizard.common.noDescription')
    const isManual = risk.source === 'manual'

    return (
      <label
        key={risk.id}
        className={`import-risks-wizard-row${options.checked ? ' is-checked' : ''}`}
      >
        <input
          type="checkbox"
          checked={options.checked}
          disabled={options.disabled}
          onChange={() => {
            if (!options.disabled) handleToggleAiRisk(risk.id)
          }}
        />
        <div>
          <p className="import-risks-wizard-row-title">
            {options.showNumber != null
              ? `#${options.showNumber} ${title}`
              : title}
          </p>
          <p className="import-risks-wizard-row-desc">{description}</p>
          <div className="import-risks-wizard-tags">
            <TagPriority priority={risk.criticality} />
            <TagPriorityRisks priority={risk.priority} />
            {isManual ? (
              <span className="import-risks-wizard-chip">
                {t('planImportRisksWizard.common.manuallyAdded')}
              </span>
            ) : null}
          </div>
        </div>
      </label>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      className="import-risks-wizard-dialog"
      PaperProps={{
        className: 'incyia-modal-paper import-risks-wizard-paper',
      }}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(6, 21, 36, 0.55)' },
      }}
      sx={{ zIndex: 2000 }}
    >
      <div className="incyia-modal-head">
        <div className="incyia-modal-icon" aria-hidden>
          <CloudUploadOutlined sx={{ fontSize: 22 }} />
        </div>
        <div>
          <h3>{headTitle}</h3>
          <p>{headSubtitle}</p>
        </div>
        <button
          type="button"
          className="incyia-modal-x"
          onClick={onClose}
          disabled={loading}
          aria-label={t('cancel', { defaultValue: 'Close' })}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <nav
        className="import-risks-wizard-stepper"
        aria-label={t('planImportRisksWizard.steps.label', {
          defaultValue: 'Import steps',
        })}
      >
        {stepStrip.map((item, index) => {
          const active = index === stepIndex
          const done = index < stepIndex
          const statusClass = active ? 'is-active' : done ? 'is-done' : ''
          return (
            <div
              key={item.key}
              className={`import-risks-wizard-step ${statusClass}`.trim()}
              aria-current={active ? 'step' : undefined}
            >
              <span className="import-risks-wizard-step-index" aria-hidden>
                {done ? '✓' : String(index + 1).padStart(2, '0')}
              </span>
              <span>
                <span className="import-risks-wizard-step-meta">
                  {t('planImportRisksWizard.steps.of', {
                    step: index + 1,
                    total: 3,
                    defaultValue: `Step ${index + 1} of 3`,
                  })}
                </span>
                <span className="import-risks-wizard-step-title">
                  {item.label}
                </span>
              </span>
            </div>
          )
        })}
      </nav>

      <div className="import-risks-wizard-body">
        {step === 'review' && (
          <>
            {allReviewRisks.length === 0 ? (
              <div className="import-risks-wizard-empty">
                {t('planImportRisksWizard.errors.noRisksDetected')}
              </div>
            ) : (
              <div className="import-risks-wizard-list">
                {allReviewRisks.map((risk, index) => {
                  const isManual = risk.source === 'manual'
                  return renderRiskRow(risk, {
                    checked: isManual || selectedRiskIds.has(risk.id),
                    disabled: isManual,
                    showNumber: index + 1,
                  })
                })}
              </div>
            )}
          </>
        )}

        {step === 'addMissing' && (
          <>
            <button
              type="button"
              className="import-risks-wizard-link-btn"
              onClick={() => setIsAiListExpanded((prev) => !prev)}
            >
              {isAiListExpanded
                ? t('planImportRisksWizard.step2.hideDetectedRisks')
                : t('planImportRisksWizard.step2.showDetectedRisks', {
                    count: aiRisks.length,
                  })}
            </button>

            <Collapse in={isAiListExpanded}>
              <div
                className="import-risks-wizard-list"
                style={{ maxHeight: 180, marginBottom: 12 }}
              >
                {aiRisks.map((risk) => (
                  <div
                    key={risk.id}
                    className="import-risks-wizard-row is-readonly"
                  >
                    <div>
                      <p className="import-risks-wizard-row-title">
                        {sanitizeRiskText(risk.name) ||
                          t('planImportRisksWizard.common.unnamedRisk')}
                      </p>
                      <div className="import-risks-wizard-tags">
                        <TagPriority priority={risk.criticality} />
                        <TagPriorityRisks priority={risk.priority} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Collapse>

            <p className="import-risks-wizard-section-title">
              {t('planImportRisksWizard.step2.addRiskSectionTitle')}
            </p>
            <div className="import-risks-wizard-form">
              <div
                className={`import-risks-wizard-field${
                  manualNameError ? ' is-error' : ''
                }`}
              >
                <label htmlFor="import-risk-name">
                  {t('planImportRisksWizard.form.name')}
                </label>
                <input
                  id="import-risk-name"
                  value={manualName}
                  onChange={(e) => {
                    setManualName(e.target.value)
                    if (manualNameError) setManualNameError('')
                  }}
                  required
                />
                {manualNameError ? (
                  <p className="import-risks-wizard-field-error">
                    {manualNameError}
                  </p>
                ) : null}
              </div>

              <div className="import-risks-wizard-field">
                <label htmlFor="import-risk-description">
                  {t('planImportRisksWizard.form.description')}
                </label>
                <textarea
                  id="import-risk-description"
                  rows={3}
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                />
              </div>

              <div className="import-risks-wizard-field-row">
                <SelectPriorityRisks
                  value={manualPriority}
                  onChange={handleManualPriorityChange}
                  portalTargetSelector=".import-risks-wizard-dialog"
                />
                <SelectCriticality
                  value={manualCriticality}
                  onChange={handleManualCriticalityChange}
                  portalTargetSelector=".import-risks-wizard-dialog"
                />
                <DatePicker
                  value={manualDeadline}
                  onChange={handleManualDeadlineChange}
                  placeholder={t('Deadline')}
                  oneTap
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <DetailButton variant="primary" size="sm" onClick={handleAddManualRisk}>
                  {t('planImportRisksWizard.step2.addToList')}
                </DetailButton>
              </div>
            </div>

            <p className="import-risks-wizard-section-title">
              {t('planImportRisksWizard.step2.manuallyAddedTitle')}
            </p>
            {manualRisks.length === 0 ? (
              <div className="import-risks-wizard-empty">
                {t('planImportRisksWizard.step2.noManualRisks')}
              </div>
            ) : (
              <div className="import-risks-wizard-manual-list">
                {manualRisks.map((risk) => (
                  <div key={risk.id} className="import-risks-wizard-manual-row">
                    <div>
                      <p className="import-risks-wizard-row-title">
                        {sanitizeRiskText(risk.name) ||
                          t('planImportRisksWizard.common.unnamedRisk')}
                      </p>
                      <p className="import-risks-wizard-row-desc">
                        {sanitizeRiskText(risk.description) ||
                          t('planImportRisksWizard.common.noDescription')}
                      </p>
                      <div className="import-risks-wizard-tags">
                        <TagPriority priority={risk.criticality} />
                        <TagPriorityRisks priority={risk.priority} />
                        <span className="import-risks-wizard-chip">
                          {t('Deadline')}: {formatDeadline(risk.deadline)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="import-risks-wizard-remove"
                      onClick={() => handleRemoveManualRisk(risk.id)}
                      aria-label={t('Delete', { defaultValue: 'Remove' })}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {step === 'final' && (
          <>
            {selectedForImport.length === 0 ? (
              <div className="import-risks-wizard-empty">
                {t('planImportRisksWizard.errors.noRisksDetected')}
              </div>
            ) : (
              <div className="import-risks-wizard-list">
                {selectedForImport.map((risk) => {
                  const title =
                    sanitizeRiskText(risk.name) ||
                    t('planImportRisksWizard.common.unnamedRisk')
                  const description = sanitizeRiskText(risk.description)
                  return (
                    <div
                      key={risk.id}
                      className="import-risks-wizard-row is-readonly"
                    >
                      <div>
                        <p className="import-risks-wizard-row-title">{title}</p>
                        {description ? (
                          <p className="import-risks-wizard-row-desc">
                            {description}
                          </p>
                        ) : null}
                        <div className="import-risks-wizard-tags">
                          <TagPriority priority={risk.criticality} />
                          <TagPriorityRisks priority={risk.priority} />
                          {risk.source === 'manual' ? (
                            <span className="import-risks-wizard-chip">
                              {t('planImportRisksWizard.common.manuallyAdded')}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {nameMissingInFinal ? (
              <p className="import-risks-wizard-warning">
                {t('planImportRisksWizard.step3.warningMissingName')}
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className="import-risks-wizard-foot">
        {step === 'review' && (
          <>
            <span className="import-risks-wizard-foot-meta">
              {t('planImportRisksWizard.step1.selectedCount', {
                selected: selectedCount,
                total: allReviewRisks.length,
              })}
            </span>
            <div className="import-risks-wizard-foot-actions">
              <DetailButton variant="ghost" onClick={() => setStep('addMissing')}>
                {t('planImportRisksWizard.step1.somethingMissing')}
              </DetailButton>
              <DetailButton
                variant="primary"
                onClick={() => setStep('final')}
                disabled={selectedCount === 0}
              >
                {t('planImportRisksWizard.step1.confirmImport')}
              </DetailButton>
            </div>
          </>
        )}

        {step === 'addMissing' && (
          <>
            <span className="import-risks-wizard-foot-meta">
              {t('planImportRisksWizard.step2.totalCount', {
                total: aiRisks.length + manualRisks.length,
              })}
            </span>
            <div className="import-risks-wizard-foot-actions">
              <DetailButton variant="ghost" onClick={() => setStep('review')}>
                {t('planImportRisksWizard.step2.backToReview')}
              </DetailButton>
              <DetailButton variant="primary" onClick={() => setStep('final')}>
                {t('planImportRisksWizard.step2.proceedToConfirm')}
              </DetailButton>
            </div>
          </>
        )}

        {step === 'final' && (
          <div className="import-risks-wizard-foot-actions">
            <DetailButton variant="ghost" onClick={onClose} disabled={loading}>
              {t('planImportRisksWizard.step3.cancel')}
            </DetailButton>
            <DetailButton
              variant="primary"
              onClick={handleConfirmImport}
              disabled={
                loading || selectedForImport.length === 0 || nameMissingInFinal
              }
            >
              {t('planImportRisksWizard.step3.confirmImport')}
            </DetailButton>
          </div>
        )}
      </div>
    </Dialog>
  )
}

export default ImportRisksWizard
